import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, onBeforeUnmount, reactive, watch } from 'vue';
import { DEFAULT_OTP_EXPIRY_TIME, otpMessage } from 'ah-common-lib/src/helpers/otp';
import { FormDefinition, FormEvent } from 'ah-common-lib/src/form/interfaces';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { getChildModel, makeFormModel, setState } from 'ah-common-lib/src/form/helpers';
import { otpField } from 'ah-common-lib/src/form/models';
import { SECOND } from 'ah-common-lib/src/constants/time';
import { MFARequest, MFAResponse, MFAState, MFAType, AuthErrorCodes } from 'ah-api-gateways';
import { getServices } from '@/app/services';
import { tap } from 'rxjs/operators';
import { debounce, DebouncedFunc } from 'lodash';


export default /*#__PURE__*/_defineComponent({
  __name: 'MFAForm',
  props: {
    staticMFA: { type: [Boolean, String], default: false },
    destination: null,
    autoRequest: { type: [String, Boolean], default: false },
    value: null,
    mfa: null
  },
  emits: ["update:value", "update:status"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'update:value', value: string): void;
  (e: 'update:status', value: MFAState): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { staticMFA: boolean | string, destination?: string, autoRequest: string | boolean, value?: string, mfa: MFARequest };





const mfaForm = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'mfaForm',
    fieldType: 'form',
    fields: [otpField('code', '', { hideErrors: true, fieldWrapperClass: 'col col-12 mb-0' })],
  }),
  validation: null,
});

const state = reactive<{
  mfaResponse?: MFAResponse;
  minutesToExpire: number;
  codeExpiryTimeout: number | null;
  status: MFAState;
  value: string;
}>({
  minutesToExpire: DEFAULT_OTP_EXPIRY_TIME,
  codeExpiryTimeout: null,
  status: MFAState.NOT_REQUESTED,
  value: '',
});

const debouncedSubmit: DebouncedFunc<(value: string) => void> = debounce(submitCode, 300);

const requestManager = useRequestManager({
  exposeToParent: true,
  onRetryFromParentManager: (k: string) => {
    if (k === `refreshMFA-${props.mfa.type}`) {
      refreshMFA();
    }
  },
}).manager;

const services = getServices();

const isEmail = computed(() => props.mfa.type === MFAType.EMAIL_OTP);

const showResendCode = computed(
  () => state.status !== MFAState.SUCCESS && requestManager.requestStates[`submitMFA-${props.mfa.type}`] !== 'pending'
);

function mfaRequest() {
  return services.auth.refreshMfa(props.mfa).pipe(tap(setResponse));
}

function checkMfaRequest(value: string) {
  return services.auth.mfa(value, props.mfa).pipe(tap(setResponse));
}

function setResponse(res: MFAResponse) {
  state.mfaResponse = { ...res };
  setStatus(res.state);
  clearExpiryTimeout();
  // FIXME handling API transition, to be removed after change
  const expiry = (res as any).expiresIn || res.expiresSeconds;
  if (expiry) {
    state.codeExpiryTimeout = window.setTimeout(() => {
      if (state.status !== MFAState.SUCCESS) {
        setStatus(MFAState.EXPIRED);
      }
    }, expiry * SECOND);
    state.minutesToExpire = expiry / 60;
  }
}

function setStatus(status: MFAState) {
  state.status = status;
}

function setError(e: any) {
  if ([AuthErrorCodes.FAILED_MFA_VALUE].includes(e.response?.data?.code)) {
    setStatus(MFAState.INVALID);
  } else {
    setStatus(MFAState.EXPIRED);
  }
}

function submitCode(value: string) {
  requestManager.currentOrNew(`submitMFA-${props.mfa.type}`, checkMfaRequest(value)).subscribe(setResponse, setError);
}

function refreshMFA() {
  if (state.status === MFAState.SUCCESS) {
    return;
  }
  mfaForm.form.code = [];
  requestManager.currentOrNew(`refreshMFA-${props.mfa.type}`, mfaRequest()).subscribe(setResponse, setError);
}

function onFormEvent(event: FormEvent) {
  if (event.event === 'form-field-set-value') {
    state.value = (mfaForm.form.code ?? []).join('');
    emit('update:value', state.value);
  }
}

function clearExpiryTimeout() {
  if (state.codeExpiryTimeout !== null) {
    clearTimeout(state.codeExpiryTimeout);
    state.codeExpiryTimeout = null;
  }
}

watch(
  () => state.status,
  () => {
    setState(getChildModel(mfaForm.form, 'code')!, 'inputClass', `form-control ${state.status.toLowerCase()}`);
    emit('update:status', state.status);
    if ([MFAState.SUCCESS, MFAState.EXPIRED, MFAState.NOT_REQUESTED].includes(state.status)) {
      setState(mfaForm.form, 'readonly', true);
      clearExpiryTimeout();
    } else {
      setState(mfaForm.form, 'readonly', false);
    }
  }
);

watch(
  () => state.value,
  (newValue: string, oldValue: string) => {
    if (newValue !== oldValue && state.value.length >= 6 && props.autoRequest !== false) {
      debouncedSubmit(state.value);
    }
  }
);

watch(
  () => props.value,
  () => (state.value = props.value ?? '')
);

onBeforeMount(refreshMFA);
onBeforeUnmount(() => {
  debouncedSubmit.cancel();
  clearExpiryTimeout();
});

expose({ setStatus, refreshMFA, setError });

return { __sfc: true,emit, props, mfaForm, state, debouncedSubmit, requestManager, services, isEmail, showResendCode, mfaRequest, checkMfaRequest, setResponse, setStatus, setError, submitCode, refreshMFA, onFormEvent, clearExpiryTimeout, otpMessage, MFAState }
}

})